<style>
    @keyframes highlight {
        0% {
            background-color: white;
        }
        25% {
            background-color: #f7f7f7;
        }
        50% {
            background-color: #f7f7f7;
        }
        75% {
            background-color: #f7f7f7;
        }
        100% {
            background-color: white;
        }
    }
    .highlighted {
        animation: highlight 0.5s;
    }

    @keyframes shakeup {
        0% {
            transform: translateY(0);
        }
        25% {
            transform: translateY(-2px);
        }
        50% {
            transform: translateY(-4px);
        }
        75% {
            transform: translateY(-6px);
        }
        100% {
            transform: translateY(-8px);
        }
    }
    .shake-up {
        animation: shakeup 0.2s ease-in-out forwards;
    }

    @keyframes shakedown {
        0% {
            transform: translateY(0);
        }
        25% {
            transform: translateY(2px);
        }
        50% {
            transform: translateY(4px);
        }
        75% {
            transform: translateY(6px);
        }
        100% {
            transform: translateY(8px);
        }
    }
    .shake-down {
        animation: shakedown 0.2s ease-in-out forwards;
    }
    .mtapp-mfp button.mtapp-disabled-button {
        cursor: initial !important;
        opacity: 0.6 !important;
        border-color: #a5a5a5 !important;
        background-color: #ffffff !important;
    }
    .mtapp-mfp button.mtapp-reset-style {
        background: none;
        border: none;
        padding: 0;
        margin: 0;
        cursor: pointer;
    }
    .mtapp-mfp button[disabled] {
        cursor: initial !important;
        opacity: 0.6 !important;
    }
    /* Slide Window */
    .mtapp-slide-window {
        overflow-y: auto;
    }
    .card-icon-container {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 100%;
        position: relative;
    }
    .mt-icon--secondary {
        width: 20px;
        height: 20px;
    }
    .card-img-top {
        min-height: 50px;
        display: flex;
        justify-content: center;
        align-items: center;
        background-color: #f8f9fa;
    }
</style>

<script>
    import { onMount, untrack } from 'svelte';
    import MultiFieldProAsset from './MultiFieldProAsset.svelte';
    import MultiFieldProContentData from './MultiFieldProContentData.svelte';
    import MultiFieldProInput from './MultiFieldProInput.svelte';
    import MultiFieldProTable from './MultiFieldProTable.svelte';
    import MultiFieldProTableVertical from './MultiFieldProTableVertical.svelte';
    import MultiFieldProMultipurpose from './MultiFieldProMultipurpose.svelte';
    import { createNewItem } from '../multiFieldPro/createNewItem.js';
    import { modalConfirm } from '../methods/modalConfirm.js';
    import { modalMsg } from '../methods/modalMsg.js';
    import { randomString } from '../methods/randomString.js';
    import { isEmpty } from '../methods/isEmpty.js';
    import { submitButtonControl } from '../methods/submitButtonControl.js';

    // ----------------------------------------
    // props で受け取る変数

    /**
     * @typedef {Object} Props
     * @property {any} mtappVars - ----------------------------------------
     * @property {any} data
     * @property {any} fieldBlocks
     * @property {any} isRequired
     * @property {any} objectType
     * @property {any} options
     */

    /** @type {Props} */
    let { mtappVars: mtappVarsProp, data = $bindable(), fieldBlocks, isRequired, objectType, options = $bindable() } = $props();
    // mtappVars はコンポーネント生存中は不変。untrack で初期値に固定する。
    const mtappVars = untrack(() => mtappVarsProp);

    // ----------------------------------------
    // ローカル変数
    // ----------------------------------------
    let staticPath = mtappVars.static_path;
    let isDebug = $state(options.debug);
    let allExpanded = $state(true);
    let insertIndex = $state(-1);
    let insertDirection = $state('before');
    let hasMultiFieldComponents = typeof window?.customElements?.get('multifield-components') !== 'undefined';
    let fieldSearchText = $state('');
    let sidebarCollapse = document.getElementById('mtapp-sidebar-collapse');
    // 各ウェブコンポーネントへの参照を保持する配列
    let webComponents = $state([]);
    let itemsLength = data.items.length;

    // ----------------------------------------
    // リアクティブな変数
    // ----------------------------------------
    let jsonData = $state('');
    let disabledAdd = $derived(options.maxBlocks > 0 && options.maxBlocks <= data.items.length);
    let disabledDuplicate = $derived(options.maxBlocks > 0 && options.maxBlocks <= data.items.length);
    let disabledRemove = $derived(Number(options.minBlocks) > 0 && Number(options.minBlocks) >= data.items.length);
    let slideWindowActive = $state(typeof options.slideWindow !== 'undefined' && options.slideWindow && options.showAddContainer);
    let showAddContainer = $state(options.showAddContainer);

    // data が変更されたら jsonData を更新
    $effect(() => {
        jsonData = JSON.stringify(data);
    });

    // ----------------------------------------
    // メソッド
    // ----------------------------------------
    /**
     * ブロックを上に移動する。
     * @param {number} i
     */
    function blockUp(i) {
        data.items[i - 1].shakeDown = true;
        data.items[i].shakeUp = true;
        data = { ...data };
        setTimeout(() => {
            data.items.splice(i - 1, 2, data.items[i], data.items[i - 1]);
            delete data.items[i - 1].shakeUp;
            delete data.items[i].shakeDown;
            data = { ...data };
        }, 200);
    }

    /**
     * ブロックを下に移動する。
     * @param {number} i
     */
    function blockDown(i) {
        data.items[i].shakeDown = true;
        data.items[i + 1].shakeUp = true;
        data = { ...data };
        setTimeout(() => {
            data.items.splice(i, 2, data.items[i + 1], data.items[i]);
            delete data.items[i].shakeUp;
            delete data.items[i + 1].shakeDown;
            data = { ...data };
        }, 200);
    }

    /**
     * ブロックを挿入する。
     * @param {string} handle
     * @param {number} index
     * @param {string} direction
     */
    function blockAdd(handle, index = -1, direction = 'after') {
        if (options.maxBlocks && options.maxBlocks <= data.items.length) {
            modalMsg({
                type: options.maxBlocksMessage.type,
                title: options.maxBlocksMessage.title,
                content: options.maxBlocksMessage.content.replace(/%maxblocks/gi, options.maxBlocks)
            });
            return;
        }
        const newItem = createNewItem(fieldBlocks, handle);
        if (index === -1) {
            data.items.push(newItem);
        } else {
            let pointerIndex = index + (direction === 'after' ? 1 : 0);
            data.items.splice(pointerIndex, 0, newItem);
        }
        data = data;
    }

    /**
     * ブロックを複製する。
     * @param {number} i
     */
    function blockDuplicate(i) {
        if (options.maxBlocks > 0 && options.maxBlocks <= data.items.length) {
            modalMsg({
                type: options.maxBlocksMessage.type,
                title: options.maxBlocksMessage.title,
                content: options.maxBlocksMessage.content.replace(/%maxblocks/gi, options.maxBlocks)
            });
            return;
        }
        const newItem = JSON.parse(JSON.stringify(data.items[i]));
        modalConfirm({
            type: 'info',
            title: '確認',
            content: `${fieldBlocks[newItem.handle].label}を複製しますか？`,
            okButtonLabel: 'OK',
            cancelButtonLabel: 'キャンセル',
            callback: function () {
                newItem.id = randomString();
                // 汎用フィールドの場合
                if (fieldBlocks[newItem.handle].type === 'multipurpose') {
                    // 連想配列をループ
                    Object.entries(newItem.data).forEach(([groupHandle, fieldsInGroup]) => {
                        fieldsInGroup.forEach((_, idx) => {
                            newItem.data[groupHandle][idx].id = randomString();
                        });
                    });
                }
                data.items.splice(i + 1, 0, newItem);
                data = { ...data };
            }
        });
    }
    /**
     * ブロックを削除する。
     * @param {number} i
     */
    function blockRemove(i) {
        modalConfirm({
            type: 'warning',
            title: '警告',
            content: '削除しますか？',
            okButtonLabel: 'OK',
            cancelButtonLabel: 'キャンセル',
            callback: function () {
                const id = data.items[i].id;
                const handle = data.items[i].handle;
                if (fieldBlocks[handle].required) {
                    delete mtappVars.__system.multiFieldErrors[id];
                    if (isEmpty(mtappVars.__system.multiFieldErrors)) {
                        submitButtonControl('submittable');
                    }
                }
                data.items.splice(i, 1);
                data = { ...data };
            }
        });
    }

    /**
     * フィールドのラベルを取得する。
     * @param {string} handle
     */
    function addButtonLabel(handle) {
        if (typeof fieldBlocks[handle] === 'undefined') {
            throw new Error(`The "${handle}" field is not found.`);
        }
        return fieldBlocks[handle].addButtonLabel || fieldBlocks[handle].label;
    }

    /**
     * マルチカラムのカラムのクラスを設定する。
     * @param {object} column
     */
    function setColumnClass(column) {
        const classes = ['mb-5', 'col'];
        if (column.col) {
            classes.push('col-' + column.col);
        }
        if (column.class) {
            classes.push(column.class);
        }
        return classes.join(' ');
    }

    /**
     * フィールドを追加するモーダルを表示する。
     */
    function showAddFieldModal(index) {
        insertIndex = index;
        const modalElement = document.getElementById('mtapp-mf-modal-' + options.id);
        const modal = new bootstrap.Modal(modalElement);
        modal.show();
    }

    /**
     * フィールドを開閉する。
     * @param {number} i
     */
    function toggleCollapsed(i) {
        data.items[i].expanded = !data.items[i].expanded;
        data = data;
    }

    /**
     * フィールドをまとめて開閉する。
     * @param state
     */
    function toggleAllCollapsed(state) {
        if (state === 'collapse') {
            allExpanded = false;
        } else if (state === 'expand') {
            allExpanded = true;
        }
        data.items.forEach((item) => {
            item.expanded = allExpanded;
        });
        data = data;
    }

    /**
     * ウェブコンポーネントにデータを渡す。
     */
    function setDataToWebComponents() {
        for (const [id, component] of Object.entries(webComponents)) {
            if (!component) {
                delete webComponents[id];
                continue;
            }
            const item = data.items.find((item) => item.id === id);
            component.mtappVars = mtappVars;
            component.item = item;
            component.id = item.id;
            component.storage = item;
            component.storageKey = data;
            component.handle = item.handle;
            component.fieldBlocks = fieldBlocks;
        }
    }

    /**
     * textarea の JSON を読み込んで data に反映
     */
    function updateFromJson() {
        try {
            data = JSON.parse(jsonData);
        } catch (e) {
            alert('Invalid JSON format');
            console.error('Invalid JSON:', e);
        }
    }

    onMount(() => {
        itemsLength = data.items.length;
        for (const item of data.items) {
            if (item.expanded === false) {
                allExpanded = false;
                break;
            }
        }
        setDataToWebComponents();
    });

    $effect(() => {
        if (data.items.length > itemsLength) {
            setDataToWebComponents();
        }
        itemsLength = data.items.length;

        if (options?.slideWindow && slideWindowActive) {
            sidebarCollapse.style.display = 'none';
            document.body.style.overflow = 'hidden';
        } else {
            window.setTimeout(() => {
                sidebarCollapse.style.display = 'block';
                document.body.style.overflow = '';
            }, 300);
        }
    });
</script>

<div class="card mtapp-mf-container mb-5 mtapp-mfp">
    <!-- Card Header -->
    <div class="card-header d-flex justify-content-between align-items-center">
        <div>
            <span class="fw-bold">{options.label}</span>
            {#if isRequired}
                <span class="badge badge-danger">{mtappVars.translate['Required']}</span>
            {/if}
            {#if isDebug}
                <span class="badge badge-warning">{mtappVars.translate['Debug Mode']}</span>
                <span class="badge badge-warning">{`v${options.version}`}</span>
            {/if}
        </div>
        {#if allExpanded}
            <button
                type="button"
                class="btn btn-default btn-sm d-flex align-items-center gap-2"
                onclick={() => {
                    toggleAllCollapsed('collapse');
                }}>
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    fill="currentColor"
                    class="bi bi-arrows-collapse"
                    viewBox="0 0 16 16">
                    <path
                        fill-rule="evenodd"
                        d="M1 8a.5.5 0 0 1 .5-.5h13a.5.5 0 0 1 0 1h-13A.5.5 0 0 1 1 8Zm7-8a.5.5 0 0 1 .5.5v3.793l1.146-1.147a.5.5 0 0 1 .708.708l-2 2a.5.5 0 0 1-.708 0l-2-2a.5.5 0 1 1 .708-.708L7.5 4.293V.5A.5.5 0 0 1 8 0Zm-.5 11.707-1.146 1.147a.5.5 0 0 1-.708-.708l2-2a.5.5 0 0 1 .708 0l2 2a.5.5 0 0 1-.708.708L8.5 11.707V15.5a.5.5 0 0 1-1 0v-3.793Z"
                    ></path>
                </svg>
                <span>{mtappVars.translate['Collapse All']}</span>
            </button>
        {:else}
            <button
                type="button"
                class="btn btn-default btn-sm d-flex align-items-center gap-2"
                onclick={() => {
                    toggleAllCollapsed('expand');
                }}>
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    fill="currentColor"
                    class="bi bi-arrows-expand"
                    viewBox="0 0 16 16">
                    <path
                        fill-rule="evenodd"
                        d="M1 8a.5.5 0 0 1 .5-.5h13a.5.5 0 0 1 0 1h-13A.5.5 0 0 1 1 8ZM7.646.146a.5.5 0 0 1 .708 0l2 2a.5.5 0 0 1-.708.708L8.5 1.707V5.5a.5.5 0 0 1-1 0V1.707L6.354 2.854a.5.5 0 1 1-.708-.708l2-2ZM8 10a.5.5 0 0 1 .5.5v3.793l1.146-1.147a.5.5 0 0 1 .708.708l-2 2a.5.5 0 0 1-.708 0l-2-2a.5.5 0 0 1 .708-.708L7.5 14.293V10.5A.5.5 0 0 1 8 10Z"
                    ></path>
                </svg>
                <span>{mtappVars.translate['Expand All']}</span>
            </button>
        {/if}
    </div>
    <!-- END Card Header -->

    <!-- Card Body -->
    <div class="card-body">
        <div class:d-none={!isDebug}>
            <textarea
                rows="10"
                class="form-control mb-3 font-monospace"
                name={objectType === 'content_data' ? 'content-field-' + options.id : options.id}
                bind:value={jsonData}></textarea>
            <p class="text-right">
                <button type="button" class="btn btn-default btn-sm" onclick={updateFromJson}>
                    {mtappVars.translate['Apply JSON changes']}
                </button>
            </p>
        </div>

        <div class="row">
            {#each data.items as item, itemIdx (item.id)}
                <div class="my-4">
                    <div
                        class="mt-collapse"
                        id={item.id + '-container'}
                        class:shake-up={typeof item.shakeUp !== 'undefined' && item.shakeUp}
                        class:shake-down={typeof item.shakeDown !== 'undefined' && item.shakeDown}>
                        <div class="mt-collapse__container">
                            <div class="col pl-4 fw-bold">
                                {fieldBlocks[item.handle].label}
                                {#if fieldBlocks[item.handle].required}
                                    <span class="badge badge-danger">{mtappVars.translate['Required']}</span>
                                {/if}
                            </div>
                            <div class="col-auto d-flex align-items-center">
                                {#if options.showSortButton}
                                    {#if itemIdx > 0}
                                        <button
                                            class="mtapp-reset-style"
                                            type="button"
                                            tabindex="0"
                                            onclick={() => {
                                                blockUp(itemIdx);
                                            }}
                                            aria-label="Move up">
                                            <svg role="img" class="mt-icon--secondary">
                                                <use xlink:href={staticPath + '/images/sprite.svg#ic_tri-up'}></use>
                                            </svg>
                                        </button>
                                    {:else}
                                        <svg role="img" class="mt-icon--secondary opacity-50">
                                            <use xlink:href={staticPath + '/images/sprite.svg#ic_tri-up'}></use>
                                        </svg>
                                    {/if}
                                    {#if itemIdx < data.items.length - 1}
                                        <button
                                            class="mtapp-reset-style"
                                            type="button"
                                            tabindex="0"
                                            onclick={() => {
                                                blockDown(itemIdx);
                                            }}
                                            aria-label="Move down">
                                            <svg role="img" class="mt-icon--secondary">
                                                <use xlink:href={staticPath + '/images/sprite.svg#ic_tri-down'}></use>
                                            </svg>
                                        </button>
                                    {:else}
                                        <svg role="img" class="mt-icon--secondary opacity-50">
                                            <use xlink:href={staticPath + '/images/sprite.svg#ic_tri-down'}></use>
                                        </svg>
                                    {/if}
                                {/if}
                                {#if options.showInsertButton !== false}
                                    <button
                                        class="mtapp-reset-style"
                                        type="button"
                                        tabindex="0"
                                        onclick={() => {
                                            if (options.slideWindow) {
                                                insertIndex = itemIdx;
                                                slideWindowActive = true;
                                            } else {
                                                showAddFieldModal(itemIdx);
                                            }
                                        }}
                                        aria-label="Add">
                                        <svg role="img" class="mt-icon--secondary">
                                            <use xlink:href={staticPath + '/images/sprite.svg#ic_add'}></use>
                                        </svg>
                                    </button>
                                {/if}
                                {#if options.showDuplicateButton !== false}
                                    <button
                                        class="mtapp-reset-style"
                                        type="button"
                                        tabindex="0"
                                        disabled={disabledDuplicate}
                                        onclick={() => {
                                            blockDuplicate(itemIdx);
                                        }}
                                        aria-label="Duplicate">
                                        <svg role="img" class="mt-icon--secondary">
                                            <use xlink:href={staticPath + '/images/sprite.svg#ic_duplicate'}></use>
                                        </svg>
                                    </button>
                                {/if}
                                {#if options.showRemoveButton !== false}
                                    <button
                                        class="mtapp-reset-style"
                                        type="button"
                                        tabindex="0"
                                        disabled={disabledRemove}
                                        onclick={() => {
                                            blockRemove(itemIdx);
                                        }}
                                        aria-label="Remove">
                                        <svg role="img" class="mt-icon--secondary">
                                            <use xlink:href={staticPath + '/images/sprite.svg#ic_trash'}></use>
                                        </svg>
                                    </button>
                                {/if}
                                <a
                                    data-bs-toggle="collapse"
                                    href={'#' + item.id + '-content'}
                                    aria-expanded={item.expanded}
                                    aria-controls={item.id + '-content'}
                                    class="d-inline-block"
                                    onclick={() => {
                                        toggleCollapsed(itemIdx);
                                    }}
                                    aria-label="Toggle">
                                    <svg role="img" class="mt-icon--secondary">
                                        <use xlink:href={staticPath + '/images/sprite.svg#ic_collapse'}></use>
                                    </svg>
                                </a>
                            </div>
                        </div>
                        <div
                            class="collapse mt-collapse__content"
                            class:show={item.expanded}
                            id={item.id + '-content'}
                            data-handle={item.handle}>
                            {#if fieldBlocks[item.handle].description}
                                <div class="mb-3">{fieldBlocks[item.handle].description}</div>
                            {/if}
                            {#if ['text', 'email', 'url', 'tel', 'password', 'number', 'datepicker', 'date', 'time', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'splitField', 'textarea', 'code', 'tinymce', 'select', 'select-multiple', 'checkbox', 'radio'].includes(fieldBlocks[item.handle].type)}
                                <MultiFieldProInput
                                    mtappVars={mtappVars}
                                    id={item.id}
                                    storage={item}
                                    storageKey="data"
                                    handle={item.handle}
                                    fieldBlock={fieldBlocks[item.handle]}
                                    on:inputchange={() => (data = { ...data })} />
                            {:else if ['asset', 'image'].includes(fieldBlocks[item.handle].type)}
                                <MultiFieldProAsset
                                    mtappVars={mtappVars}
                                    id={item.id}
                                    storage={item}
                                    storageKey="data"
                                    handle={item.handle}
                                    fieldBlock={fieldBlocks[item.handle]}
                                    on:inputchange={() => (data = { ...data })} />
                            {:else if fieldBlocks[item.handle].type === 'contentData'}
                                <MultiFieldProContentData
                                    mtappVars={mtappVars}
                                    id={item.id}
                                    storage={item}
                                    storageKey="data"
                                    handle={item.handle}
                                    fieldBlock={fieldBlocks[item.handle]}
                                    on:inputchange={() => (data = { ...data })} />
                            {:else if fieldBlocks[item.handle].type === 'table'}
                                <MultiFieldProTable
                                    mtappVars={mtappVars}
                                    id={item.id}
                                    storage={item}
                                    storageKey="data"
                                    handle={item.handle}
                                    fieldBlocks={fieldBlocks}
                                    on:inputchange={() => (data = { ...data })} />
                            {:else if ['table-vertical', 'table-vertical-fixed'].includes(fieldBlocks[item.handle].type)}
                                <MultiFieldProTableVertical
                                    mtappVars={mtappVars}
                                    id={item.id}
                                    storage={item}
                                    storageKey="data"
                                    handle={item.handle}
                                    fieldBlocks={fieldBlocks}
                                    on:inputchange={() => (data = { ...data })} />
                            {:else if fieldBlocks[item.handle].type === 'multipurpose'}
                                <MultiFieldProMultipurpose
                                    mtappVars={mtappVars}
                                    id={item.id}
                                    storage={item}
                                    storageKey="data"
                                    handle={item.handle}
                                    fieldBlocks={fieldBlocks}
                                    on:inputchange={() => (data = { ...data })} />
                            {:else if fieldBlocks[item.handle].type === 'multi-column'}
                                <div class="row" id={item.id}>
                                    {#each fieldBlocks[item.handle].columns as column, columnIdx (`${item.id}-${columnIdx}`)}
                                        <div class={setColumnClass(column)} data-column={columnIdx}>
                                            {#if column.label}
                                                <div class="mtapp-mf-column-label mb-3 mb-0">{@html column.label}</div>
                                            {/if}
                                            {#if column.description}
                                                <div class="mtapp-mf-column-description mb-3 mb-0">{@html column.description}</div>
                                            {/if}

                                            {#each column.fields as handleInColumnFields, fieldsIdx (`${item.id}-${columnIdx}-${handleInColumnFields}-${fieldsIdx}`)}
                                                <div class="my-3">
                                                    {#if fieldBlocks[handleInColumnFields].label}
                                                        <p class="mb-3 fw-bold">
                                                            {fieldBlocks[handleInColumnFields].label}
                                                            {#if fieldBlocks[handleInColumnFields].required}
                                                                <span class="badge badge-danger">{mtappVars.translate['Required']}</span>
                                                            {/if}
                                                        </p>
                                                    {/if}
                                                    {#if fieldBlocks[handleInColumnFields].description}
                                                        <p class="mb-3">{@html fieldBlocks[handleInColumnFields].description}</p>
                                                    {/if}

                                                    {#if ['text', 'email', 'url', 'tel', 'password', 'number', 'datepicker', 'date', 'time', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'splitField', 'textarea', 'code', 'tinymce', 'select', 'select-multiple', 'checkbox', 'radio'].includes(fieldBlocks[handleInColumnFields].type)}
                                                        <MultiFieldProInput
                                                            mtappVars={mtappVars}
                                                            id={`${item.id}-${columnIdx}-${handleInColumnFields}-${fieldsIdx}`}
                                                            storage={item.data}
                                                            storageKey={handleInColumnFields}
                                                            handle={handleInColumnFields}
                                                            fieldBlock={fieldBlocks[handleInColumnFields]}
                                                            on:inputchange={() => (data = { ...data })} />
                                                    {:else if ['asset', 'image'].includes(fieldBlocks[handleInColumnFields].type)}
                                                        <MultiFieldProAsset
                                                            mtappVars={mtappVars}
                                                            id={`${item.id}-${columnIdx}-${handleInColumnFields}-${fieldsIdx}`}
                                                            storage={item.data}
                                                            storageKey={handleInColumnFields}
                                                            handle={handleInColumnFields}
                                                            fieldBlock={fieldBlocks[handleInColumnFields]}
                                                            on:inputchange={() => (data = { ...data })} />
                                                    {:else if fieldBlocks[handleInColumnFields].type === 'contentData'}
                                                        <MultiFieldProContentData
                                                            mtappVars={mtappVars}
                                                            id={`${item.id}-${columnIdx}-${handleInColumnFields}-${fieldsIdx}`}
                                                            storage={item.data}
                                                            storageKey={handleInColumnFields}
                                                            handle={handleInColumnFields}
                                                            fieldBlock={fieldBlocks[handleInColumnFields]}
                                                            on:inputchange={() => (data = { ...data })} />
                                                    {:else if fieldBlocks[handleInColumnFields].type === 'table'}
                                                        <MultiFieldProTable
                                                            mtappVars={mtappVars}
                                                            id={`${item.id}-${columnIdx}-${handleInColumnFields}-${fieldsIdx}`}
                                                            storage={item.data}
                                                            storageKey={handleInColumnFields}
                                                            handle={handleInColumnFields}
                                                            fieldBlocks={fieldBlocks}
                                                            on:inputchange={() => (data = { ...data })} />
                                                    {:else if ['table-vertical', 'table-vertical-fixed'].includes(fieldBlocks[handleInColumnFields].type)}
                                                        <MultiFieldProTableVertical
                                                            mtappVars={mtappVars}
                                                            id={`${item.id}-${columnIdx}-${handleInColumnFields}-${fieldsIdx}`}
                                                            storage={item.data}
                                                            storageKey={handleInColumnFields}
                                                            handle={handleInColumnFields}
                                                            fieldBlocks={fieldBlocks}
                                                            on:inputchange={() => (data = { ...data })} />
                                                    {/if}
                                                </div>
                                            {/each}
                                        </div>
                                    {/each}
                                </div>
                            {:else if hasMultiFieldComponents}
                                <multifield-components bind:this={webComponents[item.id]} oninputchange={() => (data = { ...data })}
                                ></multifield-components>
                            {/if}
                            {#if fieldBlocks[item.handle].help}
                                <div class="mt-3 text-muted">{fieldBlocks[item.handle].help}</div>
                            {/if}
                        </div>
                    </div>
                </div>
            {/each}
        </div>
    </div>
    <!-- END Card Body -->

    <!-- Card Footer -->
    {#if (!disabledAdd || options.maxBlocksType !== 'hidden') && (options.showAddFieldButton || options.showViewRawDataButton || options.showAddContainer)}
        <div class="card-footer">
            {#if options.showAddFieldButton || options.showViewRawDataButton}
                <div class="btn-group" role="group">
                    {#if options.showAddFieldButton}
                        <button
                            type="button"
                            class="btn btn-default d-flex align-items-center"
                            onclick={() => {
                                if (options?.slideWindow) {
                                    showAddContainer = true;
                                    slideWindowActive = true;
                                } else {
                                    showAddContainer = !showAddContainer;
                                }
                            }}>
                            <svg role="img" class="mt-icon">
                                <use xlink:href={staticPath + '/images/sprite.svg#ic_add'}></use>
                            </svg>
                            {options.addFieldButtonLabel || mtappVars.translate['Add Field']}
                        </button>
                    {/if}
                    {#if options.showViewRawDataButton}
                        <button
                            type="button"
                            class="btn d-flex align-items-center"
                            class:btn-primary={isDebug}
                            class:btn-default={!isDebug}
                            onclick={() => (isDebug = !isDebug)}>
                            <svg role="img" class="mt-icon">
                                <use xlink:href={staticPath + '/images/sprite.svg#ic_preview'}></use>
                            </svg>
                            {mtappVars.translate['View Raw Data']}
                        </button>
                    {/if}
                </div>
            {/if}
            {#if showAddContainer && !options?.slideWindow}
                {#each options.fieldGroups as fieldGroup, fieldGroupIdx (fieldGroupIdx)}
                    {#if Array.isArray(fieldGroup)}
                        <div class="btn-group d-block my-3" role="group">
                            {#each fieldGroup as handle (handle)}
                                <button
                                    type="button"
                                    class="btn btn-default"
                                    class:mtapp-disabled-button={disabledAdd}
                                    onclick={() => {
                                        blockAdd(handle);
                                    }}>
                                    {addButtonLabel(handle)}
                                </button>
                            {/each}
                        </div>
                    {/if}
                {/each}
            {/if}
        </div>
    {/if}
    <!-- END Card Footer -->
</div>

<!-- モーダル -->
<div class="modal fade" role="dialog" id={'mtapp-mf-modal-' + options.id} aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered modal-lg" role="document">
        <div class="modal-content">
            <div class="modal-header">
                <h5 class="modal-title">{options.addFieldButtonLabel || mtappVars.translate['Add Field']}</h5>
            </div>
            <div class="modal-body">
                <div class="form-check form-check-inline">
                    <input
                        id={'mtapp-mf-modal-ins-above-' + options.id}
                        class="form-check-input"
                        type="radio"
                        bind:group={insertDirection}
                        value="before"
                        checked />
                    <label class="form-check-label" for={'mtapp-mf-modal-ins-above-' + options.id}>
                        {mtappVars.translate['Insert above']}
                    </label>
                </div>
                <div class="form-check form-check-inline">
                    <input
                        id={'mtapp-mf-modal-ins-below-' + options.id}
                        class="form-check-input"
                        type="radio"
                        bind:group={insertDirection}
                        value="after" />
                    <label class="form-check-label" for={'mtapp-mf-modal-ins-below-' + options.id}>
                        {mtappVars.translate['Insert below']}
                    </label>
                </div>
                <hr class="me-2" />
                {#each options.fieldGroups as fieldGroup, fieldGroupIdx (fieldGroupIdx)}
                    {#if Array.isArray(fieldGroup)}
                        <div class="btn-group d-block my-3" role="group">
                            {#each fieldGroup as handle (handle)}
                                <button
                                    type="button"
                                    class="btn btn-default"
                                    data-bs-dismiss="modal"
                                    class:mtapp-disabled-button={disabledAdd}
                                    onclick={() => {
                                        blockAdd(handle, insertIndex, insertDirection);
                                    }}>
                                    {addButtonLabel(handle)}
                                </button>
                            {/each}
                        </div>
                    {/if}
                {/each}
            </div>
            <div class="modal-footer">
                <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
                    {mtappVars.translate['Cancel']}
                </button>
            </div>
        </div>
    </div>
</div>
<!-- END モーダル -->

<div class="mtapp-slide-window-container" class:active={slideWindowActive}>
    <div class="mtapp-slide-window">
        <div class="row mt-4 mx-4" role="group">
            <div class="col-12">
                <input class="form-control" bind:value={fieldSearchText} placeholder={mtappVars.translate.Search} />
            </div>
        </div>
        <div class="row mt-4 mx-4" role="group">
            {#each options.fieldGroups as fieldGroup, fieldGroupIdx (fieldGroupIdx)}
                {#if Array.isArray(fieldGroup)}
                    <div class="w-100" style="height: 1px;"></div>
                    {#each fieldGroup as handle (handle)}
                        <div
                            class="col-12 col-sm-4 col-md-3 col-lg-2 mb-4"
                            class:d-none={fieldSearchText !== '' && !fieldBlocks[handle].label.includes(fieldSearchText)}>
                            <div
                                role="button"
                                tabindex="0"
                                class="card"
                                onclick={() => {
                                    blockAdd(handle, insertIndex, 'before');
                                    insertIndex = -1;
                                    slideWindowActive = false;
                                    window.setTimeout(() => {
                                        fieldSearchText = '';
                                    }, 300);
                                }}
                                onkeydown={() => {}}>
                                {#if fieldBlocks[handle]?.fieldImageUrl}
                                    <img src={fieldBlocks[handle].fieldImageUrl} class="card-img-top" alt={addButtonLabel(handle)} />
                                {:else}
                                    <div class="card-img-top card-icon-container">
                                        <svg role="img" class="mt-icon--secondary opacity-50" style="width: 20px; height: 20px;">
                                            <use xlink:href={staticPath + '/images/sprite.svg#ic_customfield'}></use>
                                        </svg>
                                    </div>
                                {/if}
                                <div class="card-body">
                                    <h5 class="card-title">{addButtonLabel(handle)}</h5>
                                    {#if fieldBlocks[handle].fieldDescription}
                                        <p class="card-text">{@html fieldBlocks[handle].fieldDescription}</p>
                                    {/if}
                                </div>
                            </div>
                        </div>
                    {/each}
                {/if}
            {/each}
        </div>
    </div>
    <div class="mtapp-slide-window-footer">
        <button
            type="button"
            class="btn btn-secondary ml-3"
            onclick={() => {
                insertIndex = -1;
                slideWindowActive = false;
                window.setTimeout(() => {
                    fieldSearchText = '';
                }, 300);
            }}>{mtappVars.translate['Cancel']}</button>
    </div>
</div>
